"use client";

import { Core } from "@faire/design-tokens";
import { HorizontalLine } from "@faire/web--source/slate/Layout";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import ClickOutside from "@faire/web--source/ui/ClickOutside";
import * as React from "react";
import styled, { css } from "styled-components";

export interface IPropTypes<OptionT, ValueT> {
  specialOptionsCount?: number;
  className?: string;
  classNameDropdown?: string;
  classNameItem?: string;
  style?: any;
  options: OptionT[];
  open: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  getLabel: (option: OptionT) => string;
  getValue: (option: OptionT) => ValueT;
  onSelect?: (value: ValueT) => any;
  /** Additional content that gets appended to the dropdown after all items */
  appendage?: React.ReactNode;
  renderOption?: (
    option: OptionT,
    onSelect: React.MouseEventHandler<HTMLElement>,
    onMouseOver: React.MouseEventHandler<HTMLElement>,
    selectedValue?: ValueT
  ) => React.ReactNode;
  height?: string;
  getRef?: (ref: HTMLDivElement) => any;
  allowPropogation?: boolean;
  emptyMessage?: string;
  center?: boolean;
}

/**
 * @trackcomponent
 */

export const DropdownList = <OptionT, ValueT>(
  props: React.PropsWithChildren<IPropTypes<OptionT, ValueT>>
) => {
  const {
    className,
    classNameDropdown,
    classNameItem,
    style,
    specialOptionsCount,
    renderOption,
    options,
    open,
    getLabel,
    getValue,
    appendage,
    onClose,
    children,
    height,
    getRef,
    emptyMessage,
    center,
    allowPropogation,
    onSelect,
    onOpen,
  } = props;
  const [highlightIndex, setHighlightIndex] = React.useState(0);
  const refs = React.useRef<Record<number, HTMLButtonElement | null>>({});

  const handleSelectDropDown = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    event.preventDefault();
    if (!allowPropogation) {
      event.stopPropagation();
    }
    select(index);
  };

  const select = (selectIndex: number) => {
    const selectedOption = options[selectIndex];
    const value = selectedOption ? getValue(selectedOption) : undefined;
    if (value !== undefined) {
      if (onSelect) {
        onSelect(value);
      } else if (onClose) {
        onClose();
      }
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" && open) {
      event.preventDefault();
      event.stopPropagation();
      select(highlightIndex);
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (onClose) {
        onClose();
      }
    }
  };

  const scrollIntoView = (index: number) => {
    const ref = refs.current[index];
    if (!ref) {
      return;
    }
    ref.scrollIntoView({
      block: "nearest",
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const optionLength = options.length;

    // Allow the enter key to go through, so that user can use enter key to select dropdown option
    const isRelevantKey =
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "Enter";

    if (!isRelevantKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.key === "Enter") {
      return;
    }

    if (open) {
      let nextIndex = highlightIndex;
      switch (event.key) {
        case "ArrowDown":
          nextIndex = (highlightIndex + 1) % optionLength;
          break;
        case "ArrowUp":
          nextIndex =
            highlightIndex - 1 < 0
              ? highlightIndex - 1 + optionLength
              : highlightIndex - 1;
          break;
        default:
          break;
      }

      scrollIntoView(nextIndex);
      setHighlightIndex(nextIndex);
    } else {
      onOpen?.();
    }
  };

  const onHighlight = (index: number) => () => {
    setHighlightIndex(index);
  };

  const highlightedOption = options[highlightIndex];
  const highlightValue = highlightedOption
    ? getValue(highlightedOption)
    : undefined;

  return (
    <Wrapper className={className} style={style}>
      <ClickOutside
        onClickOutside={onClose}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      >
        {children}
        {/* Do not show any dropdown components if nothing in the dropdown. */}
        {open && (options.length || emptyMessage) ? (
          <React.Fragment>
            <Dropdown
              className={classNameDropdown}
              style={{ height }}
              data-test-id="dropdown_wrapper"
            >
              {!options.length && emptyMessage && (
                <DropdownItem
                  tabIndex={-1}
                  className={classNameItem}
                  $center={center}
                >
                  {emptyMessage}
                </DropdownItem>
              )}
              {options.map((option, index) => {
                if (renderOption) {
                  return renderOption(
                    option,
                    (event) => handleSelectDropDown(event, index),
                    onHighlight(index),
                    highlightValue
                  );
                }

                const label = getLabel(option);
                const value = getValue(option);

                return (
                  <React.Fragment key={`${label} ${index}`}>
                    {!!specialOptionsCount && index === specialOptionsCount && (
                      <HorizontalLine />
                    )}
                    <DropdownItem
                      ref={(el: HTMLButtonElement | null) => {
                        if (!el) {
                          return;
                        }
                        refs.current[index] = el;
                      }}
                      tabIndex={-1}
                      className={classNameItem}
                      onMouseDown={(event) =>
                        handleSelectDropDown(event, index)
                      }
                      onMouseOver={onHighlight(index)}
                      $highlight={index === highlightIndex}
                      data-test-id={`dropdownItem_${value}`}
                      $center={center}
                    >
                      {label}
                    </DropdownItem>
                  </React.Fragment>
                );
              })}
              {appendage}
            </Dropdown>
            <div ref={getRef} />
          </React.Fragment>
        ) : null}
      </ClickOutside>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
  cursor: pointer;

  .error {
    width: 100%;
    display: block;
    ${TypographyStyles({ color: "alert" })};
    font-size: 10px;
    letter-spacing: 0.4px;
    padding: 10px 0;
    text-align: left;
  }

  &.disabled {
    opacity: 0.6;
  }

  &.disabled input {
    cursor: not-allowed !important;
    &:focus {
      border: solid 1px ${Core.border.muted};
    }
  }
`;

const DropdownStyles = css`
  display: block;
  width: 100%;
  height: auto;
  max-height: 230px;
  background-color: ${Core.surface.inverse};
  border: solid 1px ${Core.border.muted};
  position: absolute;
  bottom: 0px;
  transform: translate(0, 100%);
  overflow: auto;
`;

const Dropdown = styled.div`
  ${DropdownStyles};
  z-index: 2;
`;

const DropdownItem = styled.button<{
  $highlight?: boolean;
  $center?: boolean;
}>`
  height: 43px;
  border: 0;
  user-select: none;
  background: transparent;
  width: 100%;
  display: block;
  cursor: pointer;

  text-align: left;
  padding: 0 15px;

  ${({ $center }) =>
    $center
      ? css`
          ${TypographyStyles({ align: "center" })};
        `
      : ""};

  ${({ $highlight }) =>
    $highlight
      ? css`
          background-color: ${Core.surface.subdued};
        `
      : ""};
`;

export default DropdownList;
